// Main-thread wrapper around the simulation worker. The UI never imports `Sim` directly: it holds one
// `SimClient`, feeds it order cards, and receives snapshots and events. See plan Task 12.

import type { AoMeta, CardId, Dials, Event, Scenario, Snapshot, Target, Unit } from './types';
import type { FromWorker, RulesetInfo, TicksPerSec, ToWorker } from './worker';
import type { OrderLogEntry, TruthFrame } from './sim';
import { DEBUG } from '../debug';

export type SnapshotHandler = (snap: Snapshot, events: Event[]) => void;
export type EndedHandler = (
  snap: Snapshot, events: Event[], truth: TruthFrame[], orderLog: OrderLogEntry[], ruleset: RulesetInfo,
) => void;

export class SimClient {
  private worker: Worker | null = null;
  private snapshotHandlers: SnapshotHandler[] = [];
  private endedHandlers: EndedHandler[] = [];
  private resolveInit: (() => void) | null = null;
  private resolveTruth: ((units: Unit[]) => void) | null = null;

  /**
   * Spawns the worker, hands it the area-of-operations grid buffers and the scenario, and resolves once
   * the worker has answered with its first snapshot (tick 0, everyone in their start positions).
   */
  init(
    meta: AoMeta,
    buffers: { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer },
    scenario: Scenario,
    dials: Dials,
    seed: number,
  ): Promise<void> {
    this.dispose();
    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.worker = worker;
    worker.onmessage = (e: MessageEvent<FromWorker>) => this.receive(e.data);

    const ready = new Promise<void>((resolve) => { this.resolveInit = resolve; });
    this.post({ type: 'init', meta, buffers, scenario, dials, seed });
    return ready;
  }

  /**
   * `0` pauses the clock. The speed chips (spec pitch 2, "time you can feel") are real sim:real
   * ratios - `1/6` ticks/s is 1:1, `10/6` is 10:1, `10` is 60:1 (the old 1x), `40` is 240:1 (the old 4x) -
   * see the named constants in src/state/store.ts.
   */
  setSpeed(ticksPerSec: TicksPerSec): void {
    this.post({ type: 'speed', ticksPerSec });
  }

  issue(unitId: number, card: CardId, target: Target, playId?: string): void {
    this.post({ type: 'issue', unitId, card, target, playId });
  }

  /** Issues a named bundle of order cards; every order carries the play's id for the debrief. */
  issuePlay(playId: string, orders: { unitId: number; card: CardId; target: Target }[]): void {
    this.post({ type: 'issuePlay', playId, orders });
  }

  cancel(unitId: number): void {
    this.post({ type: 'cancel', unitId });
  }

  /**
   * Ground truth: every unit, red included.
   *
   * The ordinary snapshot carries blue's force only (src/sim/sim.ts `snapshot`), so this is the one way
   * to reach red's real positions while a fight is running - and it answers with an empty list unless
   * the page is in development or was opened with `?debug`. The end-to-end specs that drive a scout at
   * "the nearest red" use it; nothing in the product does.
   */
  truthUnits(): Promise<Unit[]> {
    if (!DEBUG || !this.worker) return Promise.resolve([]);
    return new Promise<Unit[]>((resolve) => {
      this.resolveTruth = resolve;
      this.post({ type: 'truth' });
    });
  }

  onSnapshot(cb: SnapshotHandler): void { this.snapshotHandlers.push(cb); }

  onEnded(cb: EndedHandler): void { this.endedHandlers.push(cb); }

  /** Terminates the worker and drops every handler. Safe to call when nothing is running. */
  dispose(): void {
    if (this.worker) { this.worker.terminate(); this.worker = null; }
    this.resolveInit = null;
    this.resolveTruth = null;
  }

  private post(msg: ToWorker): void {
    this.worker?.postMessage(msg);
  }

  private receive(msg: FromWorker): void {
    if (msg.type === 'truth') {
      const r = this.resolveTruth;
      this.resolveTruth = null;
      r?.(msg.units);
      return;
    }
    if (msg.type === 'snapshot') {
      if (this.resolveInit) { const r = this.resolveInit; this.resolveInit = null; r(); }
      for (const cb of this.snapshotHandlers) cb(msg.snap, msg.newEvents);
      return;
    }
    for (const cb of this.endedHandlers) cb(msg.snap, msg.events, msg.truth, msg.orderLog, msg.ruleset);
  }
}
